import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  dialogCloseIconSx,
  dialogPaperSx,
  dialogTitleSx,
  primaryBtnSx,
  secondaryBtnSx,
} from "./schedulerDialogStyles";
import { sortByStylistOrder } from "./stylistColumnOrder";

function reorderList(list, fromIndex, toIndex) {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= list.length ||
    toIndex >= list.length
  ) {
    return list;
  }
  const next = [...list];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

const RearrangeColumnsModal = ({
  open,
  stylists = [],
  stylistOrder = [],
  saving = false,
  onClose,
  onSave,
}) => {
  const [rows, setRows] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const dragIndexRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setRows(sortByStylistOrder(stylists, stylistOrder));
    setDragIndex(null);
    dragIndexRef.current = null;
  }, [open]);

  const handleDragStart = (index) => (event) => {
    dragIndexRef.current = index;
    setDragIndex(index);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragOver = (index) => (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    const fromIndex = dragIndexRef.current;
    if (fromIndex === null || fromIndex === index) {
      return;
    }
    setRows((current) => reorderList(current, fromIndex, index));
    dragIndexRef.current = index;
    setDragIndex(index);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    dragIndexRef.current = null;
    setDragIndex(null);
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    setDragIndex(null);
  };

  const handleSave = () => {
    onSave?.(rows);
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography fontWeight={700} color="inherit" sx={{ fontSize: "1.1rem" }}>
          Rearrange columns
        </Typography>
        <IconButton
          edge="end"
          onClick={onClose}
          aria-label="close"
          disabled={saving}
          sx={dialogCloseIconSx}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5, pb: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Drag and drop a stylist to change the order of scheduler columns.
        </Typography>
        {rows.length === 0 ? (
          <Typography variant="body2">No stylists to rearrange.</Typography>
        ) : (
          <Box
            component="ul"
            sx={{
              listStyle: "none",
              m: 0,
              p: 0,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "6px",
              overflow: "hidden",
              maxHeight: 420,
              overflowY: "auto",
            }}
          >
            {rows.map((stylist, index) => {
              const isDragging = dragIndex === index;
              return (
                <Box
                  key={stylist.user_id}
                  component="li"
                  draggable
                  onDragStart={handleDragStart(index)}
                  onDragOver={handleDragOver(index)}
                  onDrop={handleDrop}
                  onDragEnd={handleDragEnd}
                  aria-label={`Move ${stylist.user_name || "stylist"}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 1.25,
                    cursor: "grab",
                    bgcolor: isDragging
                      ? "rgba(197,160,111,0.18)"
                      : (theme) =>
                          theme.palette.mode === "dark" ? "#141416" : "white",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&:last-child": { borderBottom: "none" },
                    "&:active": { cursor: "grabbing" },
                    "&:hover": {
                      bgcolor: isDragging
                        ? "rgba(197,160,111,0.18)"
                        : (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.06)"
                              : "rgba(0,0,0,0.03)",
                    },
                  }}
                >
                  <DragIndicatorIcon
                    fontSize="small"
                    sx={{ color: "text.secondary", flexShrink: 0 }}
                  />
                  <Typography sx={{ fontWeight: 600 }}>
                    {stylist.user_name || `Stylist ${stylist.user_id}`}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={saving} sx={secondaryBtnSx}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || rows.length === 0}
          sx={primaryBtnSx}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RearrangeColumnsModal;
